import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Alert, Box, Snackbar, Typography } from "@mui/material";
import { useAuth } from "../../hooks/AuthProvider";
import { WS_BASE_URL } from "../../config/environment";

// Real-time pings from ws/instagram/ instead of polling. Events arriving within 300 ms are merged into one
// onChange({ conversations: Set of ids (null = any), comments: bool, reconnected: bool }) call; after every
// (re)connect it fires with reconnected=true so whatever was missed while offline is refetched.
export const useInstagramSocket = (onChange) => {
  const { getValidToken } = useAuth();
  const latest = useRef({ onChange, getValidToken });
  latest.current = { onChange, getValidToken };

  useEffect(() => {
    let socket;
    let retry;
    let flush;
    let stopped = false;
    let batch = null;
    const queue = (event) => {
      batch = batch || { conversations: new Set(), comments: false, reconnected: false };
      if (event.kind === "ready") batch.reconnected = true;
      if (event.kind === "comment") batch.comments = true;
      if (event.kind === "conversation") batch.conversations.add(event.conversation ?? null);
      clearTimeout(flush);
      flush = setTimeout(() => {
        const merged = batch;
        batch = null;
        latest.current.onChange(merged);
      }, 300);
    };
    const open = () => {
      socket = new WebSocket(`${WS_BASE_URL}/ws/instagram/`);
      socket.onopen = async () => {
        try {
          const token = await latest.current.getValidToken();
          socket.send(JSON.stringify({ type: "auth", token, tenant: localStorage.getItem("tenant") }));
        } catch {
          socket.close();
        }
      };
      socket.onmessage = (e) => queue(JSON.parse(e.data));
      // 4003 = not allowed for this tenant: retrying won't help
      socket.onclose = (e) => {
        if (!stopped && e.code !== 4003) retry = setTimeout(open, 3000);
      };
    };
    open();
    return () => {
      stopped = true;
      clearTimeout(retry);
      clearTimeout(flush);
      socket.close();
    };
  }, []);
};

// Helpers shared by the Instagram Flow pages and Channel Onboarding.

export const STATUS_CHIP = {
  CONNECTED: { label: "Connected", color: "success" },
  NEEDS_REAUTH: { label: "Needs reconnect", color: "error" },
  MESSAGING_DISABLED: { label: "Messaging off", color: "warning" },
  DISCONNECTED: { label: "Disconnected", color: "default" },
  ERROR: { label: "Error", color: "error" },
};

export const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

export const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (data?.error) return data.error;
  if (data && typeof data === "object") {
    const [field, msgs] = Object.entries(data)[0] || [];
    if (field) return `${field}: ${[].concat(msgs).join(" ")}`;
  }
  return fallback;
};

export const Card = ({ children, sx }) => (
  <Box sx={{ bgcolor: "#ffffff", border: "1px solid #eceff1", borderRadius: "12px", p: 2.5, mb: 2, ...sx }}>
    {children}
  </Box>
);

// White card with an optional step number, title and one-line hint (automation builder, account settings).
export const Section = ({ step, title, hint, action, children, sx }) => (
  <Box sx={{ bgcolor: "#fff", border: "1px solid #eceff1", borderRadius: "12px", p: { xs: 2, sm: 3 }, ...sx }}>
    <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
      {step && (
        <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#f1ebfd", color: "#7b3fe4", fontWeight: 700,
          fontSize: "0.85rem", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {step}
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        <Typography fontWeight={700} fontSize="1rem">{title}</Typography>
        {hint && <Typography variant="body2" color="text.secondary">{hint}</Typography>}
      </Box>
      {action}
    </Box>
    <Box display="flex" flexDirection="column" gap={2.5}>{children}</Box>
  </Box>
);

// Authenticated request + snackbar notifications.
export const useInstagramApi = () => {
  const { getValidToken } = useAuth();
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const notify = useCallback(
    (message, severity = "success") => setSnackbar({ open: true, message, severity }),
    [],
  );

  const request = useCallback(
    async (method, url, data) => {
      const token = await getValidToken();
      const headers = { Authorization: `Bearer ${token}`, "X-Tenant-ID": localStorage.getItem("tenant") };
      return (await axios({ method, url, data, headers })).data;
    },
    [getValidToken],
  );

  const toast = (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={4000}
      onClose={() => setSnackbar({ ...snackbar, open: false })}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  return { request, notify, toast };
};
